import React, { useState, useRef, useEffect } from 'react';
import { X, Palette } from 'lucide-react';
import { ActiveInput, ActiveLabel, DisabledInput, DisabledLabel, ErrorInput, ErrorLabel } from './Util.jsx';
import InfoBox from './InfoBox.jsx';

const ColorPicker = ({
    label,
    value,
    setValue,
    error = false,
    disabled = false,
    readOnly = false,
    classes = '',
    info = false,
    infotext = '',
    infopos = 'r',
    infoclass = '',
    infosize = 20
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const inputRef = useRef(null);
    const pickerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target) &&
                inputRef.current && !inputRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleInputClick = () => {
        if (!disabled && !readOnly) {
            setIsOpen(!isOpen);
        }
    };

    const handleClear = (e) => {
        e.stopPropagation();
        setValue('');
        setIsOpen(false);
    };

    const handleColorSelect = (color) => {
        setValue(color);
        setIsOpen(false);
    };

    // Colori predefiniti per una palette rapida
    const defaultColors = {
        "gray": ["#000000", "#424242", "#797979", "#A9A9A9", "#D6D6D6", "#FFFFFF"],
        "other": [
            ["#0433FF", "#515EFF", "#9297FF", "#CBCDFF"],
            ["#FF2600", "#FF5A50", "#FF9592", "#FFCCCA"],
            ["#FF40FF", "#FF65FF", "#FF9AFF", "#FFCEFF"],
            ["#00F900", "#44F950", "#8DFB92", "#CAFDCA"],
            ["#00FCFF", "#44FDFF", "#8EFDFF", "#C9FEFF"],
            ["#FEFB00", "#FEFC50", "#FFFC92", "#FFFECB"]
        ]
    }

    return (
        <div className={`${classes}`} onMouseDown={(e) => e.preventDefault()}>
            <div className="relative w-full">
                <input
                    type="text"
                    ref={inputRef}
                    className={disabled ? DisabledInput : error ? ErrorInput : ActiveInput}
                    placeholder=" "
                    value={value}
                    readOnly
                    onClick={handleInputClick}
                    onFocus={handleInputClick}
                    onChange={() => { }} // Empty to satisfy React
                />
                <label
                    className={`hover:cursor-text rounded-lg ${disabled ? DisabledLabel : error ? ErrorLabel : ActiveLabel}`}
                    onClick={() => !disabled && inputRef.current.focus()}
                >
                    {label}
                </label>

                {/* Anteprima colore */}
                {value ? (
                    <div
                        className="absolute inset-y-0 right-0 w-6 h-6 rounded border border-gray-300 dark:border-neutral-600 my-auto"
                        style={{ backgroundColor: value }}
                    />
                ) : (
                    <div className="absolute inset-y-0 right-0 flex items-center pointer-events-none text-gray-700 dark:text-neutral-300">
                        <Palette size={20} />
                    </div>
                )}

                {value && !disabled && !readOnly && (
                    <div
                        className="absolute inset-y-0 right-6 flex items-center text-gray-700 dark:text-neutral-300 hover:cursor-pointer"
                        onClick={handleClear}
                    >
                        <X size={20} />
                    </div>
                )}

                {info && (
                    <div className="absolute inset-y-0 -right-6 flex items-center hover:cursor-pointer">
                        <InfoBox text={infotext} position={infopos} size={infosize} classes={infoclass} />
                    </div>
                )}
            </div>

            {/* Picker dropdown */}
            {isOpen && (
                <div
                    ref={pickerRef}
                    className="mt-1 bg-gray-100 dark:bg-neutral-900 border-2 border-gray-300 dark:border-neutral-700 rounded-lg w-full p-3 z-50"
                >
                    {/* Input colore nativo per maggiore precisione */}
                    <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-2">
                            Seleziona colore esatto:
                        </label>
                        <input
                            type="color"
                            value={value || '#000000'}
                            onChange={(e) => setValue(e.target.value.toUpperCase())}
                            className="w-full h-10 cursor-pointer rounded border border-gray-300 dark:border-neutral-600"
                        />
                    </div>

                    {/* Palette predefinita */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-2">
                            Colori rapidi:
                        </label>
                        <div className="grid grid-cols-6 gap-1 md:gap-2">

                            {/* {defaultColors.map((color, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleColorSelect(color)}
                                    className="w-full h-6 rounded border border-gray-300 dark:border-neutral-600 hover:scale-110 transition-transform"
                                    style={{ backgroundColor: color }}
                                    title={color}
                                />
                            ))} */}
                            {defaultColors.gray.map((color, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleColorSelect(color)}
                                    className="w-full h-6 rounded border border-gray-300 dark:border-neutral-600 hover:scale-110 transition-transform"
                                    style={{ backgroundColor: color }}
                                    title={color}
                                />
                            ))}
                            {defaultColors.other.map((row, rowIndex) => (
                                <div key={rowIndex} className="flex grid grid-cols-2 gap-1">
                                    {row.map((color, index) => (
                                        <button
                                            key={index}
                                            onClick={() => handleColorSelect(color)}
                                            className="w-full h-6 rounded border border-gray-300 dark:border-neutral-600 hover:scale-110 transition-transform"
                                            style={{ backgroundColor: color }}
                                            title={color}
                                        />
                                    ))}
                                </div>
                            ))}

                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ColorPicker;